import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@connectrpc/connect-query'
import { Trans } from '@lingui/react/macro'

import { verifyEmail } from '@jotta/grpc-connect-openapi/signupQuery'
import { ErrorMessage } from '@jotta/ui/ErrorMessage'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import Spinner from '@jotta/ui/Spinner'
import { autoLogin } from '@jotta/utils/auth'

import type { EmailVerification, LoggedIn } from './CreateUser'
import { CTAButton } from './CTAButton'

export function VerifyEmail({
  verificationCode,
}: {
  verificationCode: string
}) {
  const navigate = useNavigate()
  const { mutate, data, error } = useMutation(verifyEmail, {
    throwOnError: false,
    onSuccess: ({ status }) => {
      if (status?.case === 'success') {
        const { username, autoLoginCode } = status.value
        return autoLogin({
          username,
          token: autoLoginCode,
          redirectTo: '/',
        })
      }
    },
  })
  useEffect(() => {
    mutate({
      input: {
        case: 'verificationCode',
        value: verificationCode,
      },
    })
  }, [verificationCode, mutate])

  const status = data?.status
  if ((!data && !error) || status?.case === 'success') {
    return <LoadingOverlay id="verifyEmail" open={true} />
  }

  const returnTo = (status?.case === 'failed' && status?.value.returnTo) || '/'
  return (
    <div className="flex flex-col items-center p-16">
      <ErrorMessage>
        <Trans>Email verification failed</Trans>
      </ErrorMessage>
      <button className="btn btn-primary" onClick={() => navigate(returnTo)}>
        <Trans>Continue</Trans>
      </button>
    </div>
  )
}

export function EmailVerificationStatus({
  emailVerification: { emailVerificationId, emailAddress },
  onSuccess,
  onFailure,
}: {
  emailVerification: EmailVerification
  onSuccess: (loggedIn: LoggedIn) => void
  onFailure: () => void
}) {
  const { data, error } = useQuery(
    verifyEmail,
    {
      input: {
        case: 'verificationId',
        value: emailVerificationId,
      },
    },
    {
      throwOnError: false,
      refetchInterval: 1_000,
    },
  )
  const status = data?.status
  useEffect(() => {
    if (status?.case === 'success') {
      onSuccess(status.value)
    } else if (error || status?.case === 'failed') {
      onFailure()
    }
  }, [onFailure, onSuccess, error, status])

  return (
    <>
      <Spinner />
      <div>
        <Trans>
          We just sent an email to <b>{emailAddress}</b>. Please check your
          inbox for instructions for how to complete your sign-up.
        </Trans>
      </div>
    </>
  )
}
